import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useCart, cartKey} from "../context/CartContext";
import ColorSwatch from "../components/ColorSwatch";
import CouponField from "../components/CouponField";
import {placeOrder} from "../api";
import {calcPricing, nextOfferHint, COD_FEE} from "../utils/pricing";
import "./Checkout.css";

const PAYMENT_METHODS = [
  {
    id: "cashfree",
    label: "Pay Online",
    icon: "💳",
    desc: "UPI, Debit/Credit Card, Net Banking — powered by Cashfree",
  },
  {
    id: "cod",
    label: "Cash on Delivery",
    icon: "💵",
    desc: `Pay when your order arrives. ₹${COD_FEE} COD charge applies.`,
  },
];

const INDIA_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu & Kashmir",
  "Ladakh",
];

export default function Checkout() {
  const {cart, subtotal, clearCart} = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("cashfree");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, percent} once applied

  const {shippingFee, discount, couponDiscount, codFee, freeGift, total} =
    calcPricing(subtotal, paymentMethod, coupon?.percent);
  const offerHint = nextOfferHint(subtotal);

  if (cart.length === 0) {
    return (
      <div className="checkout-empty">
        <p>Your cart is empty.</p>
        <button onClick={() => navigate("/products")}>Browse Products</button>
      </div>
    );
  }

  /* ── Validation ── */
  function validate() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.phone))
      e.phone = "Enter a valid 10-digit mobile number";
    if (!form.email.trim() || !emailRegex.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.addressLine1.trim()) e.addressLine1 = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state) e.state = "Please select a state";
    if (!/^\d{6}$/.test(form.pincode))
      e.pincode = "Enter a valid 6-digit pincode";
    return e;
  }

  function handleAddressNext() {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(2);
  }

  /* ── Place order & trigger payment ── */
  async function handlePlaceOrder() {
    setSubmitting(true);
    setPayError("");

    try {
      const orderPayload = {
        items: cart.map((i) => ({
          product: i._id,
          name: i.name,
          price: i.price,
          quantity: i.qty,
          image: i.images?.[0]?.url,
          customName: i.customName,
          color: i.color,
        })),
        shippingAddress: form,
        paymentMethod,
        couponCode: coupon?.code,
        subtotal,
        shippingFee,
        discount,
        codFee,
        total,
      };

      const {data} = await placeOrder(orderPayload);
      const {order, paymentSessionId} = data;

      // No online payment needed — go straight to success
      if (!paymentSessionId) {
        clearCart();
        navigate(`/order-success/${order.orderNumber}`);
        return;
      }

      // Online (full amount, or COD charges) — load Cashfree JS SDK and open checkout
      const cashfree = await loadCashfreeSDK();

      // Clear cart before redirect — order already saved in DB
      clearCart();

      // Redirect to Cashfree hosted payment page
      const result = await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
      });

      // If redirect didn't happen, log the result
      console.log("[Cashfree] checkout result:", result);
    } catch (err) {
      console.error(err);
      setPayError(
        err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Helpers ── */
  function set(key, val) {
    setForm((f) => ({...f, [key]: val}));
    setErrors((e) => ({...e, [key]: ""}));
  }

  function loadCashfreeSDK() {
    return new Promise((resolve, reject) => {
      // If already loaded and initialized, return existing instance
      if (window.__cashfreeInstance) {
        resolve(window.__cashfreeInstance);
        return;
      }

      // If SDK script already in DOM but not initialized
      if (window.Cashfree) {
        const instance = window.Cashfree({
          mode: process.env.REACT_APP_CASHFREE_ENV || "production",
        });
        window.__cashfreeInstance = instance;
        resolve(instance);
        return;
      }

      // Load the script fresh
      const script = document.createElement("script");
      script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      script.onload = () => {
        try {
          const instance = window.Cashfree({
            mode: process.env.REACT_APP_CASHFREE_ENV || "production",
          });
          window.__cashfreeInstance = instance;
          resolve(instance);
        } catch (e) {
          reject(new Error("Cashfree SDK init failed: " + e.message));
        }
      };
      script.onerror = () =>
        reject(new Error("Failed to load Cashfree SDK script"));
      document.head.appendChild(script);
    });
  }

  function reviewActions() {
    return (
      <>
        {payError && <div className="pay-error">⚠️ {payError}</div>}

        <button
          className="btn-place"
          onClick={handlePlaceOrder}
          disabled={submitting}
        >
          {submitting
            ? "Redirecting to payment…"
            : paymentMethod === "cod"
              ? `Pay ₹${codFee} COD Charges →`
              : `Pay ₹${total.toLocaleString("en-IN")} →`}
        </button>

        <p className="secure-note">🔒 Secured by Cashfree Payments</p>
      </>
    );
  }

  function field(name, label, opts = {}) {
    return (
      <div className="form-field" key={name}>
        <label>
          {label}
          {opts.optional ? "" : " *"}
        </label>
        {opts.type === "select" ? (
          <select
            value={form[name]}
            onChange={(e) => set(name, e.target.value)}
            className={errors[name] ? "error" : ""}
          >
            <option value="" disabled>
              Select State
            </option>
            {INDIA_STATES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        ) : (
          <input
            type={opts.type || "text"}
            value={form[name]}
            onChange={(e) => set(name, e.target.value)}
            placeholder={opts.placeholder}
            className={errors[name] ? "error" : ""}
            maxLength={opts.maxLength}
          />
        )}
        {errors[name] && <span className="field-error">{errors[name]}</span>}
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="checkout-layout">
        {/* ── Left — steps ── */}
        <div className="checkout-main">
          {/* Step indicator */}
          <div className="steps">
            {["Address", "Payment", "Review"].map((s, i) => (
              <div
                key={s}
                className={`step ${step >= i + 1 ? "done" : ""} ${step === i + 1 ? "active" : ""}`}
              >
                <span className="step-num">{step > i + 1 ? "✓" : i + 1}</span>
                <span className="step-label">{s}</span>
                {i < 2 && <span className="step-sep" />}
              </div>
            ))}
          </div>

          {/* ── Step 1: Address ── */}
          {step === 1 && (
            <div className="checkout-section">
              <h2>Shipping address</h2>
              <div className="form-grid">
                {field("name", "Full name", {
                  placeholder: "Full name",
                })}
                {field("phone", "Mobile number", {
                  placeholder: "Mobile Number",
                  maxLength: 10,
                })}
                {field("email", "Email", {
                  placeholder: "E-Mail",
                  type: "email",
                  optional: true,
                })}
                {field("addressLine1", "Address line 1", {
                  placeholder: "Flat / House no, Street",
                })}
                {field("addressLine2", "Address line 2", {
                  placeholder: "Landmark (optional)",
                  optional: true,
                })}
                {field("city", "City", {placeholder: "City"})}
                {field("state", "State", {type: "select"})}
                {field("pincode", "Pincode", {
                  placeholder: "Pin Code",
                  maxLength: 6,
                })}
              </div>
              <button className="btn-next" onClick={handleAddressNext}>
                Continue to Payment →
              </button>
            </div>
          )}

          {/* ── Step 2: Payment ── */}
          {step === 2 && (
            <div className="checkout-section">
              <h2>Payment method</h2>
              <div className="payment-methods">
                {PAYMENT_METHODS.map((pm) => (
                  <label
                    key={pm.id}
                    className={`payment-option ${paymentMethod === pm.id ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={pm.id}
                      checked={paymentMethod === pm.id}
                      onChange={() => setPaymentMethod(pm.id)}
                    />
                    <span className="pm-icon">{pm.icon}</span>
                    <div className="pm-text">
                      <strong>{pm.label}</strong>
                      <span>{pm.desc}</span>
                    </div>
                  </label>
                ))}
              </div>

              {paymentMethod === "cashfree" && (
                <div className="payment-info-box">
                  <p>
                    🔒 You'll be securely redirected to Cashfree to complete
                    payment.
                  </p>
                  <div className="payment-icons">
                    <span>UPI</span>
                    <span>Visa</span>
                    <span>Mastercard</span>
                    <span>RuPay</span>
                    <span>Net Banking</span>
                  </div>
                </div>
              )}

              {paymentMethod === "cod" && (
                <div className="payment-info-box">
                  <p>
                    💵 Pay ₹{COD_FEE} COD charges online now to confirm your
                    order, and the rest in cash when your order is delivered.
                  </p>
                </div>
              )}

              <div className="checkout-nav checkout-nav--desktop">
                <button className="btn-back" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button className="btn-next" onClick={() => setStep(3)}>
                  Review Order →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === 3 && (
            <div className="checkout-section">
              <h2>Review your order</h2>

              <div className="review-block">
                <h3>Delivering to</h3>
                <p>
                  {form.name} · {form.phone}
                </p>
                <p>
                  {form.addressLine1}
                  {form.addressLine2 ? `, ${form.addressLine2}` : ""}
                </p>
                <p>
                  {form.city}, {form.state} — {form.pincode}
                </p>
                <button className="link-btn" onClick={() => setStep(1)}>
                  Edit
                </button>
              </div>

              <div className="review-block">
                <h3>Payment</h3>
                <p>
                  {PAYMENT_METHODS.find((p) => p.id === paymentMethod)?.icon}{" "}
                  {PAYMENT_METHODS.find((p) => p.id === paymentMethod)?.label}
                </p>
                <button className="link-btn" onClick={() => setStep(2)}>
                  Edit
                </button>
              </div>

              <div className="checkout-actions checkout-actions--desktop">
                {reviewActions()}
              </div>
            </div>
          )}
        </div>

        {/* ── Right — Order summary ── */}
        <aside className="order-summary">
          <h3 className="summary-title">Order Summary</h3>
          <div className="summary-items">
            {cart.map((item) => (
              <div key={cartKey(item)} className="summary-item">
                <img
                  src={
                    item.images?.[0]?.url ||
                    item.images?.[0] ||
                    "https://placehold.co/60x60/f5f5f5/aaa?text="
                  }
                  alt={item.name}
                  className="summary-img"
                />
                <div className="summary-item-text">
                  <p className="summary-item-name">{item.name}</p>
                  {item.customName && (
                    <p className="summary-item-meta">
                      Name: {item.customName}
                    </p>
                  )}
                  {item.color && (
                    <p className="summary-item-meta">
                      Colour: <ColorSwatch color={item.color} />
                    </p>
                  )}
                  <p className="summary-item-meta">Qty: {item.qty}</p>
                </div>
                <span className="summary-item-price">
                  ₹{(item.price * item.qty).toLocaleString("en-IN")}
                </span>
              </div>
            ))}
          </div>
          <CouponField coupon={coupon} onChange={setCoupon} />
          <div className="summary-totals">
            <div className="total-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="total-row">
              <span>Shipping</span>
              <span>
                {shippingFee === 0 ? (
                  <span className="free-tag">FREE</span>
                ) : (
                  `₹${shippingFee}`
                )}
              </span>
            </div>
            {discount > 0 && (
              <div className="total-row">
                <span>Discount (10% off)</span>
                <span className="free-tag">
                  −₹{discount.toLocaleString("en-IN")}
                </span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="total-row">
                <span>
                  Coupon {coupon.code} ({coupon.percent}% off)
                </span>
                <span className="free-tag">
                  −₹{couponDiscount.toLocaleString("en-IN")}
                </span>
              </div>
            )}
            {codFee > 0 && (
              <div className="total-row">
                <span>COD charges</span>
                <span>₹{codFee}</span>
              </div>
            )}
            {freeGift && (
              <div className="total-row">
                <span>🎁 Surprise gift</span>
                <span className="free-tag">FREE</span>
              </div>
            )}
            {offerHint && <p className="free-shipping-hint">{offerHint}</p>}
            <div className="total-row grand">
              <span>Total</span>
              <span>₹{total.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </aside>

        {/* Mobile-only: Back / Review Order, shown below the order summary */}
        {step === 2 && (
          <div className="checkout-nav checkout-nav--mobile">
            <button className="btn-back" onClick={() => setStep(1)}>
              ← Back
            </button>
            <button className="btn-next" onClick={() => setStep(3)}>
              Review Order →
            </button>
          </div>
        )}

        {/* Mobile-only: Pay button + secure note, shown below the order summary */}
        {step === 3 && (
          <div className="checkout-actions checkout-actions--mobile">
            {reviewActions()}
          </div>
        )}
      </div>
    </div>
  );
}
